import {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
  type RefObject,
} from "react";
import "@css/temp_create.css";
import type * as type from "@vTypes";
import * as excel from "@script/excel.ts";
import TableHeader from "@components/feature/rosterTable/TableHeader.tsx";
import { RosterShiftLegendChips } from "@components/feature/rosterTable/RosterShiftLegendChips";
import useRosterConfig from "@/hooks/useRosterConfig";
import { useRosterDisplayShiftColumns } from "@/hooks/shift/useRosterDisplayShiftColumns";
import TableBody from "@components/feature/rosterTable/TableBody.tsx";
import ButtonGroup from "@components/feature/roster_create/ButtonGroup.tsx";
import ModalRosterModify from "@components/feature/roster_create/ModalRosterModify.tsx";
import SetDeadLine from "@components/feature/roster_create/SetDeadLine.tsx";
import LoadingDots from "@components/LoadingDots.tsx";
import ModalManpower from "@components/feature/roster_create/ModalManpower.tsx";
import Roster_create_Tab2 from "@components/feature/rosterTable/Roster_create_Tab2.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { QUERY_BASE_KEY } from "@constants/query-key.ts";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { useInitializeGroup } from "@hooks/useInitializeGroup.ts";
import { useGroupStore } from "@store/groupStore.ts";
import { useIsAdmin, useLoginUser } from "@hooks/useAuth.ts";
import { useIsHeadNurse } from "@/hooks/useHeadNurse";
import { useNavigate } from "react-router-dom";
import RosterMadeTable from "@components/feature/rosterTable/RosterMadeTable.tsx";
import { RosterViolationFloatingControls } from "@/components/feature/rosterTable/RosterViolationFloatingControls";
import NurseEditSideProfile from "@components/feature/head_nurse_mgmt/NurseEditSideProfile.tsx";
import ModalOffRequestList from "@components/feature/roster_create/components/ModalOffRequestList.tsx";
import ContainerLoadingOverlay from "@components/modal/ContainerLoadingOverlay";
import { useRosterResources } from "@hooks/useRosterMetaresource.ts";
import { useRosterInitializer } from "@hooks/useRosterInitalizer.ts";
import {
  secondsToTimeString,
  SelectVersionHandler,
} from "@utils/rosterCreate-util.ts";
import { sortNurseListByRoleThenSequence } from "@utils/common-util.ts";
import { useScheduleStore } from "@store/scheduleStore";
import useRosterVersions from "@/hooks/roster-craete/useRosterVersions";
import useRosterSchedule from "@/hooks/roster-craete/useRosterSchedule";
import { useWantedStatusQuery } from "@/hooks/roster-craete/useWantedStatusQuery";
import httpClient from "@/config/axios";
import { useHolidayDatesQuery } from "@/hooks/useHolidayDatesQuery";
import useRosterStatus from "@/hooks/zustand/useRosterStatus";
import RosterIndicater from "./components/RosterIndicater";
import RosterViewSettings from "./components/RosterViewSettings";
import { toast } from "@/components/toast/CustomToast";
import { cn } from "@/lib/utils";
import type { NurseResponse } from "@/types/nurse-type";
import ModalRosterWanted from "@/components/feature/roster_create/modal-roster-wanted";
import useGenerateRosterMutation, {
  type GenerateRosterResponse,
  RosterInfeasibleError,
  type Infeasibility,
} from "./hook/useGenerateRosterMutation";
import { invalidateRosterCachesAndSelectLatestSchedule } from "./service/invalidateRosterCreateCaches";
import type { insertPreceptorData } from "@/utils/preceptorUtils";
import { useWantedAdjustmentQuery } from "@/hooks/roster-craete/useWantedAdjustmentQuery";
import useStore from "@/store/store";
import { useRosterValidation } from "@/hooks/useRosterValidation";
import { getPrecepteePolicy } from "@/utils/precepteePolicy";
import useRosterHistory from "./hook/useRosterHistory";
import useManpower from "./hook/useManpower";
import useRosterEditing from "./hook/useRosterEditing";
import { ModifiedCellsProvider } from "./context/ModifiedCellsContext";
import useRosterPrevTail from "@/hooks/roster-craete/useRosterPrevTail";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import { useShiftLookupMaps } from "@/hooks/shift/useShiftLookupMaps";
import { useRosterCreateUiStore } from "@/store/rosterCreateUiStore";
import { useEmergencyReplacement } from "./hook/useEmergencyReplacement";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import CustomModal from "@/components/modal/CustomModal";

// 이니셜 데이터 or CopyNurses 타입 (정의는 rosterCreate-util)
export type { CopyNurses } from "@utils/rosterCreate-util.ts";
export type FullNurseSchemaAddPreceptor = ReturnType<
  typeof insertPreceptorData
>;

/** `YYYY-MM-DD`는 Date 파싱 시 UTC 자정 → 로컬 `getDate()`가 하루 밀릴 수 있음. 캘린더 일은 문자열에서만 취한다. */
function calendarDayFromShiftDate(
  shiftDate: string,
  expectedYear: number,
  expectedMonth: number
): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(shiftDate.trim());
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  if (y !== expectedYear || month !== expectedMonth) return null;
  if (!Number.isFinite(day) || day < 1 || day > 31) return null;
  return day;
}

// 최대위반 설정 on OFF
const ENABLE_MAX_MANPOWER_VIOLATION = false;

function Roster_create() {
  const loginUser = useLoginUser();
  const isAdmin = useIsAdmin();
  const isHeadNurse = useIsHeadNurse();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { curDate, curScheduleInfo, setCurScheduleInfo } = useScheduleStore();

  const wantedButtonRef = useRef<HTMLButtonElement>(null);
  const rosterModifyButtonRef = useRef<HTMLButtonElement>(null);

  // pooling 상태 받기 위한 job 생성
  const { rosterMade, rosterInfeasibility, setRosterInfeasibility } =
    useRosterStatus();

  // Group Selecter (변경은 useInitializeGroup 등에서 처리)
  const { selectedGroupId, selectedOfficeId } = useGroupStore();

  // 폴링 job SUCCESS 시 캐시 갱신 + VERSION 리패치 후 최신 스케줄 선택
  useEffect(() => {
    if (rosterMade.status !== "SUCCESS") return;
    const year = curDate?.year;
    const month = curDate?.month;
    if (year == null || month == null) return;

    let cancelled = false;

    void (async () => {
      await invalidateRosterCachesAndSelectLatestSchedule(
        queryClient,
        {
          year,
          month,
          selectedGroupId,
          isAdmin,
        },
        setCurScheduleInfo
      );
      if (cancelled) return;
    })();

    return () => {
      cancelled = true;
    };
  }, [
    rosterMade.status,
    curDate?.year,
    curDate?.month,
    selectedGroupId,
    queryClient,
    isAdmin,
    setCurScheduleInfo,
  ]);

  const gid = isAdmin ? (selectedGroupId ?? "") : "";

  const { data: shiftsDataForRosterTable } = useShiftsQuery(
    isAdmin ? (selectedGroupId ?? undefined) : undefined
  );

  const [isPrevMonthOpen, setIsPrevMonthOpen] = useState(false);

  // 이전달 prevTail 데이터 가져오기
  // 토글 시에만 호출 (enabled: isPrevMonthOpen)
  const { data: prevTailData, isFetching: isFetchingPrevTail } =
    useRosterPrevTail({
      year: curDate?.year ?? 0,
      month: curDate?.month ?? 0,
      scheduleId: curScheduleInfo?.scheduleId,
      groupId: gid ?? "",
      enabled: isPrevMonthOpen,
    });

  const { data: holidayDates = [] } = useHolidayDatesQuery(
    curDate?.year ?? undefined,
    curDate?.month ?? undefined
  );

  const effectivePrevTailData = useMemo(() => {
    if (!isPrevMonthOpen) return null;
    if (prevTailData?.nurses && prevTailData.nurses.length > 0)
      return prevTailData;

    const pMonth = curDate?.month === 1 ? 12 : (curDate?.month ?? 1) - 1;
    const pYear =
      curDate?.month === 1 ? (curDate?.year ?? 0) - 1 : (curDate?.year ?? 0);
    const prevMonthLastDay = new Date(pYear, pMonth, 0).getDate();
    const tail_days = Array.from(
      { length: 6 },
      (_, i) => prevMonthLastDay - 5 + i
    );

    return {
      nurses: [{ nurse_id: "dummy", name: "dummy", shifts: {} }],
      tail_days,
      prev_month: pMonth,
      prev_year: pYear,
    };
  }, [isPrevMonthOpen, prevTailData, curDate?.year, curDate?.month]);

  /* 모달 관련 */
  const [memo, setMemo] = useState<string>("");
  const uiMode = useRosterCreateUiStore((state) => state.mode);
  const setUiMode = useRosterCreateUiStore((state) => state.setMode);
  const resetUiMode = useRosterCreateUiStore((state) => state.resetMode);
  /** 헤더 범례 칩 + 행별 ShiftStatus 열 표시 */
  const [showPerShiftStats, setShowPerShiftStats] = useState(false);

  useEffect(
    () => () => {
      resetUiMode();
    },
    [resetUiMode]
  );

  // 로딩 상태 관리
  const [isDateChanging, setIsDateChanging] = useState<boolean>(false);
  const [isVersionChanging, setIsVersionChanging] = useState<boolean>(false);
  const [isDragEditing] = useState(false);
  const prevScheduleIdRef = useRef<string | undefined>(undefined);
  const prevRosterDataRef = useRef<typeof rosterData | undefined>(undefined);

  const [fullNurseSchema, setFullNurseSchema] = useState<NurseResponse[]>([]);

  const [fullNurseSchemaAddPreceptor, setFullNurseSchemaAddPreceptor] =
    useState<FullNurseSchemaAddPreceptor>();

  const [availableShifts, setAvailableShifts] = useState<
    type.availableShifts[]
  >([]);

  /* 근무표 수정 관련 */
  const [selectedShift, setSelectedShift] =
    useState<type.Type_availableShifts | null>(null);

  const [currentTeamTableViewTab, setCurrentTeamTableViewTab] =
    useState<number>(0);

  /* 로딩 관련 */
  const [isLoadedPage, setIsLoadedPage] = useState<boolean>(false);
  const prevDateKey = useRef<string>("");

  const [currentConfigVer, setCurrentConfigVer] = useState<number | null>(null);

  const [currentShiftManage, setCurrentShiftManage] =
    useState<type.ShiftManageObject[]>();

  // defaultShiftCode를 useMemo로 직접 계산 (기존 useState + useEffect 제거)
  const defaultShiftCode = useMemo(() => {
    if (availableShifts.length === 0) return [];
    return availableShifts;
  }, [availableShifts]);

  const { lookupMaps: rosterShiftLookupMaps } = useShiftLookupMaps(
    defaultShiftCode,
    shiftsDataForRosterTable ?? null
  );

  // ModalRosterWanted의 초기 탭 상태 관리
  const [wantedModalInitialTab, setWantedModalInitialTab] = useState<
    "view" | "settings"
  >("view");

  const [fullWanted, setFullWanted] = useState<type.WantedAll[]>([]);

  /* 버튼 클릭시 비활성화 관련 */
  const [isChangeDateButtonClick, setIsChangeDateButtonClick] =
    useState<boolean>(false);

  // 사이드 프로필 모달
  const [isSideProfileOpen, setIsSideProfileOpen] = useState<boolean>(false);
  const [activeNurseIdForSideProfile, setActiveNurseIdForSideProfile] =
    useState<string | null>(null);

  // TODO : 일단 임시로 이렇게 처리 / 원티드 조정판 열리면 부모페이지 스크롤 막음
  useEffect(() => {
    const mainContainer = document.querySelector(
      ".c_main__container"
    ) as HTMLElement;

    if (!mainContainer) return;

    if (uiMode === "wanted" || uiMode === "wantedSelector") {
      mainContainer.style.overflow = "hidden";
    } else {
      mainContainer.style.overflow = "";
    }

    return () => {
      mainContainer.style.overflow = "";
    };
  }, [uiMode]);

  const { mutate: publishRoster, isPending: isPublishPending } = useMutation({
    mutationFn: async () => {
      await httpClient.post(
        `/roster/publish?group_id=${encodeURIComponent(selectedGroupId ?? "")}`,
        {
          schedule_id: curScheduleInfo?.scheduleId ?? "",
          issue_comment: "", //?
        }
      );
      return true; //true반환해야함...
    },
    onSuccess: async () => {
      toast.success("근무표가 성공적으로 발행되었습니다!");

      // 버전 리스트 캐시 무효화
      await queryClient.invalidateQueries({
        queryKey: ["VERSIONS", curDate?.year, curDate?.month],
      });

      // 마감 근무표 초기화(연·월 단위 — `group_id`별 캐시 전부)
      await queryClient.invalidateQueries({
        queryKey: [
          "ROSTER_ISSUED_ROSTER",
          Number(curDate?.year),
          Number(curDate?.month),
        ],
      });

      // issued 캐싱 초기화
      await queryClient.invalidateQueries({
        queryKey: [QUERY_BASE_KEY.ISSUED, selectedGroupId],
      });

      // 알림 리스트 캐싱 무효화 (팝오버, 모달 모두)
      await queryClient.invalidateQueries({ queryKey: ["alarms"] });
    },
  });

  const { mutate: unPublishRoster, isPending: isUnPublishPending } =
    useMutation({
      mutationFn: async () => {
        await httpClient.post(`/roster/unpublish`, null, {
          params: { schedule_id: curScheduleInfo?.scheduleId ?? "" },
        });
      },
      onSuccess: async () => {
        toast.success("마감이 철회되었습니다.");

        // 버전 리스트 캐시 무효화
        await queryClient.invalidateQueries({
          queryKey: ["VERSIONS", curDate?.year, curDate?.month],
        });

        // 마감 근무표 초기화(연·월 단위 — `group_id`별 캐시 전부)
        await queryClient.invalidateQueries({
          queryKey: [
            "ROSTER_ISSUED_ROSTER",
            Number(curDate?.year),
            Number(curDate?.month),
          ],
        });

        // issued 캐싱 초기화
        await queryClient.invalidateQueries({
          queryKey: [QUERY_BASE_KEY.ISSUED, selectedGroupId],
        });
      },
    });

  async function scheduleDownload_excel() {
    if (!curDate?.year || !curDate?.month) {
      toast.error("날짜가 누락되었습니다.");
      return;
    }
    try {
      await excel.schedule_download(
        curScheduleInfo?.scheduleId ?? "",
        curDate?.year,
        curDate?.month,
        isAdmin ? (selectedGroupId ?? undefined) : undefined
      );
    } catch (error) {
      console.error("다운로드에 실패했습니다.", error);
      alert("다운로드에 실패했습니다.");
    }
  }

  // 근무표 생성하기 - 커스텀으로 분리함
  const {
    mutateAsync: generateRosterMutate,
    isSuccess: isGenerateRosterSuccess,
    reset: resetGenerateRoster,
  } = useGenerateRosterMutation();
  const [blockingInfeasibility, setBlockingInfeasibility] =
    useState<Infeasibility | null>(null);

  // ButtonGroup에 전달할 래퍼 함수
  useEffect(() => {
    if (rosterInfeasibility?.severity === "blocking") {
      setBlockingInfeasibility(rosterInfeasibility);
    }
  }, [rosterInfeasibility]);

  const generateRoster = async ({
    gradeStrategy,
  }: {
    gradeStrategy: string;
  }): Promise<GenerateRosterResponse | null | undefined> => {
    setBlockingInfeasibility(null);
    setRosterInfeasibility(null);

    try {
      return await generateRosterMutate({
        gradeStrategy,
        curDate,
        currentConfigVer,
        isAdmin,
        selectedGroupId,
      });
    } catch (error) {
      if (error instanceof RosterInfeasibleError) {
        setBlockingInfeasibility(error.infeasibility);
      }
      throw error;
    }
  };

  useEffect(() => {
    if (rosterMade.status === "QUEUED" || rosterMade.status === "RUNNING") {
      setBlockingInfeasibility(null);
    }
  }, [rosterMade.status]);

  useEffect(() => {
    if (isGenerateRosterSuccess) {
      const timer = setTimeout(() => {
        resetGenerateRoster();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isGenerateRosterSuccess, resetGenerateRoster]);

  // 버전 리스트는 useRosterVersions로 가져오기 (중복 호출 방지)
  const { data: versionList } = useRosterVersions({
    groupId: isAdmin ? (selectedGroupId ?? null) : null,
  });

  // 현재 그룹의 버전 목록에 존재하는 schedule_id일 때만 스케줄 조회
  // 삭제된 scheduleId는 절대 요청하지 않도록 체크
  const { deletedScheduleIds } = useScheduleStore();
  const isValidScheduleForCurrentGroup = useMemo(() => {
    if (!curScheduleInfo?.scheduleId || !versionList) return false;
    // 삭제된 scheduleId는 요청하지 않음
    if (deletedScheduleIds.has(curScheduleInfo.scheduleId)) return false;
    return versionList.some(
      (v) => v.schedule_id === curScheduleInfo.scheduleId
    );
  }, [curScheduleInfo?.scheduleId, versionList, deletedScheduleIds]);

  // Group (스토어·그룹 목록 동기화; 반환값은 이 페이지에서 미사용)
  useInitializeGroup({
    loginUser,
    isAdmin,
  });

  // StrictMode 중복 호출 및 ADM의 selectedGroupId 미준비 상태 가드
  useRosterInitializer({ groupId: gid ?? null });

  // 캐싱 데이터
  const { data, isLoading: isResourceLoading } = useRosterResources({
    groupId: gid ?? "",
  });

  // 스케줄 데이터는 useQuery로 캐싱
  const { data: rosterData, isLoading } = useRosterSchedule({
    scheduleId: curScheduleInfo?.scheduleId,
    groupId: gid ?? "",
    enabled: isValidScheduleForCurrentGroup,
  });

  // === 커스텀 훅: useRosterEditing ===
  const {
    copyNurses,
    setCopyNurses,
    isRosterEmpty,
    modifiedCells,
    setModifiedCells,
    addModifiedCell,
  } = useRosterEditing({
    rosterData,
    fullNurseSchema,
    fullNurseSchemaAddPreceptor,
    curDate,
  });

  // useEmergencyReplacement에서 nursesIndex 조회를 위해 최신 copyNurses 유지
  const copyNursesRef = useRef(copyNurses);
  copyNursesRef.current = copyNurses;

  const {
    emergencySlotPick,
    setEmergencySlotPick,
    emergencyBulkPeriodPick,
    setEmergencyBulkPeriodPick,
    emergencySingleHasResult,
    setEmergencySingleHasResult,
    emergencyBulkSelectedSteps,
    setEmergencyBulkSelectedSteps,
    emergencyBulkHasResult,
    setEmergencyBulkHasResult,
    bulkResultResetRef,
    setEmergencyReplacementPeriodMode,
    handleEmergencyReplacementCellPick,
    emergencyReplacementCandidateNurseId,
    setEmergencyReplacementCandidateNurseId,
    emergencyPostApplyHighlight,
    handleEmergencyModalOpenChange,
    openEmergencyModalFromButton,
    applyEmergencyReplacement,
    resetEmergencyOnDateChange,
  } = useEmergencyReplacement({
    setCopyNurses,
    defaultShiftCode,
    addModifiedCell,
    copyNursesRef,
  });

  // === 커스텀 훅: useRosterHistory ===
  const {
    handleUndo,
    handleRedo,
    modDayShift,
    deleteAllShift,
    canUndo,
    canRedo,
    clearHistory,
  } = useRosterHistory({
    copyNurses,
    setCopyNurses,
    addModifiedCell,
    modifiedCells,
    setModifiedCells,
  });

  // === 커스텀 훅: useManpower ===장
  const manpowerOid = isAdmin ? selectedOfficeId : loginUser?.office_id;
  const manpowerGid = isAdmin ? selectedGroupId : loginUser?.group_id;
  const {
    manPower_monthly,
    manPower_daily,
    useMaxManpower,
    manpowerOid: resolvedManpowerOid,
    manpowerGid: resolvedManpowerGid,
    year: manpowerYear,
    month: manpowerMonth,
  } = useManpower({
    manpowerOid: manpowerOid ?? undefined,
    manpowerGid: manpowerGid ?? undefined,
    year: curDate?.year ?? undefined,
    month: curDate?.month ?? undefined,
    isAdmin,
    selectedOfficeId,
    selectedGroupId,
    loginUser,
  });

  useEffect(() => {
    const currentScheduleId = curScheduleInfo?.scheduleId;
    // scheduleId가 변경되었을 때만 버전 전환 상태 설정
    if (
      prevScheduleIdRef.current !== undefined &&
      prevScheduleIdRef.current !== currentScheduleId &&
      currentScheduleId
    ) {
      setIsVersionChanging(true);
      // 버전 전환 시 히스토리 초기화 및 모달 닫기
      clearHistory();
      setUiMode("none");
      // 버전 전환 시 이전 데이터 저장 (로딩 중 표시용)
      // rosterData가 undefined가 아닐 때만 저장 (null이어도 저장)
      if (rosterData !== undefined) {
        prevRosterDataRef.current = rosterData;
      }
    }
    prevScheduleIdRef.current = currentScheduleId;
  }, [curScheduleInfo?.scheduleId, rosterData, clearHistory]);

  // 로딩 완료 시 버전 전환 상태 해제 및 이전 데이터 정리
  useEffect(() => {
    if (!isLoading && isVersionChanging) {
      setIsVersionChanging(false);
      // 새 데이터가 로드되면 (있든 없든) 이전 데이터 참조 제거
      // rosterData가 undefined가 아니면 (null이어도) 새 데이터로 간주
      if (rosterData !== undefined) {
        prevRosterDataRef.current = undefined;
      }
    }
  }, [isLoading, isVersionChanging, rosterData]);

  // 원티드 상태는 SetDeadLine에서도 필요하므로 여기서 가져오기 (ButtonGroup에서도 사용하지만 중복 호출 방지를 위해 여기서 가져옴)

  // 캐싱을 재사용하는거로 변경..
  useEffect(() => {
    if (!data) return;

    // Nurses
    if (data.nurses) {
      setFullNurseSchema(sortNurseListByRoleThenSequence([...data.nurses]));
    }

    if (data.fullNurseSchemaAddPreceptor) {
      setFullNurseSchemaAddPreceptor(data.fullNurseSchemaAddPreceptor);
    }

    // Shifts
    if (data.shifts) {
      const sortedShifts = [...data.shifts].sort(
        (a, b) => a.sequence - b.sequence
      );
      const updatedShifts = sortedShifts.map((shift) => ({
        ...shift,
        start_time: secondsToTimeString(shift.start_time),
        end_time: secondsToTimeString(shift.end_time),
        allday: Boolean(shift.allday),
        auto_schedule: Boolean(shift.auto_schedule),
      }));

      setAvailableShifts(updatedShifts);
    }

    // Config Versions
    if (data.configs) {
      setCurrentConfigVer(data.configs?.config_id || null);
    }

    // Shift Manage
    if (data.shiftManager) {
      setCurrentShiftManage(data.shiftManager);
    }

    // Wanted List
    if (data.wantedList) {
      setFullWanted(data.wantedList);
    }
    setIsLoadedPage(true);
  }, [data]);

  // 날짜 변경 감지 및 로딩 상태 관리
  const isInitialLoad = useRef<boolean>(true);

  // 날짜 변경 시 즉시 버전 초기화 (이전 날짜의 버전이 보이지 않도록)
  useEffect(() => {
    if (curDate?.year && curDate?.month) {
      const currentDateKey = `${curDate.year}-${curDate.month}`;
      const dateChanged = prevDateKey.current !== currentDateKey;

      if (dateChanged) {
        // 날짜가 변경되면 원티드 반영사항 보기를 false로 설정 (쿼리 실행 방지)
        if (showWantedViolations) toggleWantedViolations();
        // 날짜가 변경되면 위반사항 토글을 기본값(최소인원·설정 ON)으로 리셋
        if (!showManpowerViolations) toggleManpowerViolations();
        if (!showConfigViolations) toggleConfigViolations();
        // 날짜가 변경되면 히스토리 초기화 및 모달 닫기
        clearHistory();
        setUiMode("none");
        // 날짜가 변경되면 버전을 초기화하고 로딩 상태로 설정
        setCurScheduleInfo(null);
        setIsDateChanging(true);
        prevDateKey.current = currentDateKey;
        isInitialLoad.current = false; // 초기 로드가 아니면 날짜 변경으로 간주
      }
    }
  }, [curDate?.year, curDate?.month, clearHistory]);

  // 년·월 또는 선택 근무표가 바뀌면 우측 근무코드별 집계 열은 기본(닫힘)으로
  useEffect(() => {
    setShowPerShiftStats(false);
  }, [curDate?.year, curDate?.month, curScheduleInfo?.scheduleId]);

  // curDate는 있지만 curScheduleInfo가 null인 경우 자동 선택 (다른 페이지에서 돌아왔을 때)
  useEffect(() => {
    // curDate는 있지만 curScheduleInfo가 null이고, versionList가 로드되었을 때 자동 선택
    if (
      curDate?.year &&
      curDate?.month &&
      !curScheduleInfo?.scheduleId &&
      versionList &&
      versionList.length > 0
    ) {
      const selected = SelectVersionHandler(versionList, "current");
      setCurScheduleInfo(selected);
    }
  }, [curDate?.year, curDate?.month, curScheduleInfo?.scheduleId, versionList]);

  useEffect(() => {
    if (!versionList) return;

    // versionList에 있는 scheduleId들은 삭제된 것으로 표시하지 않음
    const { removeDeletedScheduleId } = useScheduleStore.getState();
    versionList.forEach((v) => {
      removeDeletedScheduleId(v.schedule_id);
    });

    if (versionList.length > 0) {
      // 초기 로드이거나 curScheduleInfo가 없을 때만 자동 선택
      // 버전 전환 시에는 이미 curScheduleInfo가 설정되어 있으므로 건드리지 않음
      if (isInitialLoad.current || !curScheduleInfo?.scheduleId) {
        const selected = SelectVersionHandler(versionList, "current");
        setCurScheduleInfo(selected);
      } else {
        // 이거 그룹 전환에 따른 안전 장치임
        const currentExists = versionList.some(
          (v) => v.schedule_id === curScheduleInfo.scheduleId
        );
        if (!currentExists) {
          const selected = SelectVersionHandler(versionList, "current");
          setCurScheduleInfo(selected);
        }
      }
    } else {
      // versionList가 비어있으면 (모든 버전이 삭제된 경우) curScheduleInfo 초기화
      setCurScheduleInfo(null);
    }

    setIsChangeDateButtonClick(false);
    setIsDateChanging(false);
    isInitialLoad.current = false;
  }, [versionList]); // curScheduleInfo를 의존성에서 제거하여 버전 전환 시 깜빡임 방지

  // 날짜 변경 시 관련 데이터 로드
  useEffect(() => {
    if (curDate?.year && curDate?.month) {
      setIsLoadedPage(true);
      setUiMode("none");
      resetEmergencyOnDateChange();
    }
  }, [curDate?.year, curDate?.month, setUiMode, resetEmergencyOnDateChange]);

  // rosterData의 memo 업데이트
  useEffect(() => {
    if (rosterData?.memo !== undefined) {
      setMemo(rosterData.memo ?? "");
    }
  }, [rosterData?.memo]);

  const { data: wantedStatus } = useWantedStatusQuery({
    year: curDate?.year ?? 0,
    month: curDate?.month ?? 0,
    groupId: gid ?? null,
  });

  // 위반사항 토글 상태 (전역 store)
  const {
    showWantedViolations,
    toggleWantedViolations,
    showManpowerViolations,
    toggleManpowerViolations,
    showConfigViolations,
    toggleConfigViolations,
  } = useStore();

  // 원티드 반영 설정 데이터 가져오기 (토글이 켜졌을 때만, 버전이 있을 때만)
  const { data: wantedAdjustmentData, isLoading: isWantedAdjustmentLoading } =
    useWantedAdjustmentQuery(
      curDate?.year,
      curDate?.month,
      showWantedViolations && !!curScheduleInfo?.scheduleId // enabled: 토글이 켜져있고 버전이 있을 때만
    );

  // copyNurses에 isAppliedMap 및 wantedEntriesMap 매핑
  const copyNursesWithWanted = useMemo(() => {
    if (!wantedAdjustmentData?.nurses || copyNurses.length === 0) {
      return copyNurses;
    }

    // wantedAdjustmentData.nurses를 Map으로 변환 (빠른 조회를 위해)
    const wantedNursesMap = new Map(
      wantedAdjustmentData.nurses.map((wantedNurse) => [
        wantedNurse.nurse_id,
        wantedNurse,
      ])
    );

    const year = curDate?.year ?? new Date().getFullYear();
    const month = curDate?.month ?? new Date().getMonth() + 1;
    const daysInMonth = new Date(year, month, 0).getDate();

    return copyNurses.map((nurse) => {
      const wantedNurse = wantedNursesMap.get(nurse.nurse_id);
      // 원티드 데이터에 있는 날짜별 근무코드 매핑 (날짜 -> shift_id)
      const wantedEntriesMap: Record<number, string> = {};
      /** 원티드 엔트리 `shifts_table_id` — 메인 `schedule_ids`와 동일한 시프트 DB id로 미일치 비교 */
      const wantedShiftsTableIdsMap: Record<number, number | null> = {};

      if (wantedNurse?.entries) {
        wantedNurse.entries.forEach((entry) => {
          if (entry.shift_date && entry.shift_id) {
            const day = calendarDayFromShiftDate(entry.shift_date, year, month);

            if (day != null && day >= 1 && day <= daysInMonth) {
              // isAppliedMap은 원티드 반영 설정 모달에서만 사용되므로 일반 화면에서는 생성하지 않음
              // 관리자가 명시적으로 원티드 반영을 거절(X)한 건(is_applied === false)은
              // 메인 테이블의 '원티드 미일치 위반' 표기에서 제외하기 위해 매핑하지 않습니다.
              if (entry.is_applied !== false) {
                wantedEntriesMap[day] = entry.shift_id;
                wantedShiftsTableIdsMap[day] =
                  entry.shifts_table_id != null &&
                  Number.isFinite(Number(entry.shifts_table_id))
                    ? Number(entry.shifts_table_id)
                    : null;
              }
            }
          }
        });
      }

      return {
        ...nurse,
        // isAppliedMap은 원티드 반영 설정 모달에서만 사용되므로 일반 화면에서는 전달하지 않음
        wantedEntriesMap, // 원티드 데이터에 있는 날짜별 근무코드
        wantedShiftsTableIdsMap,
      };
    });
  }, [copyNurses, wantedAdjustmentData, curDate?.year, curDate?.month]);

  const { data: serverConfig } = useRosterConfig();
  const isUseMid = serverConfig?.use_mid;

  // 검증용 copyNurses: 프리셉티 제외 + isAppliedMap=false(거절된 원티드) 투명화
  const precepteePolicy = getPrecepteePolicy(serverConfig);
  const validationCopyNurses = useMemo(() => {
    return (
      copyNursesWithWanted
        .filter((nurse) =>
          precepteePolicy.excludePrecepteeFromCount
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (nurse as any).preceptor?.role !== "preceptee"
            : true
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((nurse: any) => {
          if (!nurse.isAppliedMap) return nurse;
          return {
            ...nurse,
            schedule:
              nurse.schedule?.map((shift: string | null, idx: number) => {
                const day = idx + 1;
                if (nurse.isAppliedMap[day] === false) return null;
                return shift;
              }) ?? null,
          };
        })
    );
  }, [copyNursesWithWanted, precepteePolicy.excludePrecepteeFromCount]);

  // RosterMadeTable 통계용 전처리: 프리셉티 제외 + M 필터
  const statsNurses = useMemo(
    () =>
      precepteePolicy.excludePrecepteeFromCount
        ? copyNursesWithWanted.filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (n) => (n as any).preceptor?.role !== "preceptee"
          )
        : copyNursesWithWanted,
    [copyNursesWithWanted, precepteePolicy.excludePrecepteeFromCount]
  );
  const statsShiftCode = useMemo(
    () =>
      isUseMid === false
        ? defaultShiftCode.filter((s) => s.default_shift !== "M")
        : defaultShiftCode,
    [defaultShiftCode, isUseMid]
  );

  const { displayShifts: rosterCreateShiftLegend } =
    useRosterDisplayShiftColumns(
      copyNursesWithWanted as {
        schedule?: (string | null)[] | null;
        schedule_ids?: (number | null)[] | null;
      }[],
      defaultShiftCode,
      isUseMid,
      { includeBaselineMainTemplateColumns: true }
    );

  // 근무표가 비어있는지 확인 (모든 스케줄이 '-' 이거나 없는 경우) - useRosterEditing에서 가져옴

  // 발행된 근무표 상태
  const isPublished = useMemo(
    () =>
      versionList?.find((v) => v.schedule_id === curScheduleInfo?.scheduleId)
        ?.status === "issued",
    [versionList, curScheduleInfo?.scheduleId]
  );

  // modifiedCells context value 메모이제이션
  const modifiedCellsValue = useMemo(
    () => ({ modifiedCells }),
    [modifiedCells]
  );

  // currentScheduleInfo 객체 메모이제이션 (여러 곳에서 사용)
  const currentScheduleInfoMemo = useMemo(
    () => ({
      year: curDate?.year ?? 0,
      month: curDate?.month ?? 0,
      version: curScheduleInfo?.version ?? 0,
    }),
    [curDate?.year, curDate?.month, curScheduleInfo?.version]
  );

  // 검증 훅 (근무표 만들기 전용) — 결과를 RosterMadeTable에 prop으로 전달
  const {
    validateList: rawValidateListFromHook,
    cellViolationMap: rosterCellViolationMap,
    isValidating: isRosterValidating,
    phase: rosterValidationPhase,
    validate: triggerRosterValidation,
  } = useRosterValidation({
    currentScheduleInfo: currentScheduleInfoMemo,
    currentScheduleID: curScheduleInfo?.scheduleId,
    copyNurses: validationCopyNurses as Parameters<
      typeof useRosterValidation
    >[0]["copyNurses"],
    isDragEditing,
    manPower_monthly,
    manPower_daily,
    useMaxManpower,
    enableMaxManpowerViolation: ENABLE_MAX_MANPOWER_VIOLATION,
    year: curDate?.year ?? 0,
    month: curDate?.month ?? 0,
    serverConfig: serverConfig ?? null,
    defaultShiftCode,
    shiftsData: shiftsDataForRosterTable ?? [],
    disableManpowerValidation: false,
    prevTailData: effectivePrevTailData,
  });

  useEffect(() => {
    if (!isDragEditing) triggerRosterValidation();
  }, [
    validationCopyNurses,
    curScheduleInfo?.scheduleId,
    isDragEditing,
    triggerRosterValidation,
  ]);

  // 버전 변경 시 위반사항 토글 리셋
  const prevVersionRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    const ver = currentScheduleInfoMemo.version;
    if (
      prevVersionRef.current !== undefined &&
      prevVersionRef.current !== ver
    ) {
      if (showWantedViolations) toggleWantedViolations();
      if (!showManpowerViolations) toggleManpowerViolations();
      if (!showConfigViolations) toggleConfigViolations();
    }
    prevVersionRef.current = ver;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScheduleInfoMemo.version]);

  // selectIdForSideprofile 함수 메모이제이션
  const handleSelectIdForSideProfile = useCallback(
    (bool: boolean, nurseID: string) => {
      setActiveNurseIdForSideProfile(nurseID);
      setIsSideProfileOpen(bool);
    },
    []
  );

  // fetchWantedStatus 함수 메모이제이션
  const handleFetchWantedStatus = useCallback(
    (year: number, month: number) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.wanted.status(year, month, gid ?? ""),
      });
    },
    [queryClient, gid]
  );

  // ModalRosterWanted onClose 함수 메모이제이션
  const handleModalRosterWantedClose = useCallback(() => {
    // ModalRosterWanted를 닫을 때 WantedSelectorModal을 다시 열기
    setUiMode("wantedSelector");
  }, [setUiMode]);

  if (!isLoadedPage || isResourceLoading)
    return (
      <div className="rosterCreating">
        근무표를 불러오고 있습니다.
        <LoadingDots />
      </div>
    );

  const isGenerateRequesting =
    rosterMade.status === "QUEUED" || rosterMade.status === "RUNNING";
  const shouldShowWantedAdjustmentLoadingOverlay =
    isWantedAdjustmentLoading && uiMode !== "wanted" && !isGenerateRequesting;

  return (
    <ModifiedCellsProvider value={modifiedCellsValue}>
      <div
        className={cn(
          "font-pretendard   w-full",
          rosterMade.status && "overflow-hidden!"
        )}
      >
        <div className=" flex flex-col items-center gap-10  ">
          <div
            className={` flex flex-col items-center w-full  ${uiMode === "edit" ? "us-n" : ""}`}
          >
            <div className="w-full px-5 sticky justify-center   bg-white  top-0 z-20  flex  items-end">
              <div className=" pt-5 ">
                <div className=" flex flex-col">
                  <ButtonGroup
                    editModeButtonRef={rosterModifyButtonRef}
                    memo={memo}
                    copyNurse={copyNurses}
                    generateRoster={generateRoster}
                    isPublishPending={isPublishPending}
                    publishRoster={publishRoster}
                    isPublished={!!isPublished}
                    unPublishRoster={unPublishRoster}
                    isUnPublishPending={isUnPublishPending}
                    setWantedModalInitialTab={setWantedModalInitialTab}
                    scheduleDownload_excel={scheduleDownload_excel}
                    isRosterCreating={
                      rosterMade.status === "QUEUED" ||
                      rosterMade.status === "RUNNING"
                    }
                    isChangeDateButtonClick={isChangeDateButtonClick}
                    setIsChangeDateButtonClick={setIsChangeDateButtonClick}
                    currentTeamTableViewTab={currentTeamTableViewTab}
                    selectedGroupId={selectedGroupId}
                    isRosterEmpty={isRosterEmpty}
                    isWantedAdjustmentLoading={isWantedAdjustmentLoading} // 원티드 반영사항 로딩 중일 때 버튼 비활성화
                    clearHistory={clearHistory}
                    emergencySlotPick={emergencySlotPick}
                    setEmergencySlotPick={setEmergencySlotPick}
                    emergencyBulkPeriodPick={emergencyBulkPeriodPick}
                    setEmergencyBulkPeriodPick={setEmergencyBulkPeriodPick}
                    onEmergencyBulkPathStepsChange={
                      setEmergencyBulkSelectedSteps
                    }
                    onEmergencySingleResultChange={setEmergencySingleHasResult}
                    onEmergencyBulkResultChange={setEmergencyBulkHasResult}
                    emergencyBulkResultResetRef={bulkResultResetRef}
                    onEmergencyReplacementPeriodModeChange={
                      setEmergencyReplacementPeriodMode
                    }
                    setEmergencyReplacementCandidateNurseId={
                      setEmergencyReplacementCandidateNurseId
                    }
                    onApplyEmergencyReplacement={applyEmergencyReplacement}
                    availableShifts={availableShifts}
                    onEmergencyModalOpenChange={handleEmergencyModalOpenChange}
                    openEmergencyModalFromButton={openEmergencyModalFromButton}
                  />

                  <RosterViewSettings
                    currentTeamTableViewTab={currentTeamTableViewTab}
                    setCurrentTeamTableViewTab={setCurrentTeamTableViewTab}
                    isWantedAdjustmentLoading={isWantedAdjustmentLoading}
                    isPrevMonthOpen={isPrevMonthOpen}
                    setIsPrevMonthOpen={setIsPrevMonthOpen}
                    isFetchingPrevTail={isFetchingPrevTail}
                    curDate={curDate}
                  />

                  <div
                    className={cn(
                      "animate-in fade-in-0 duration-300 transition-opacity relative mb-4"
                    )}
                  >
                    <RosterMadeTable
                      defaultShiftCode={statsShiftCode}
                      shiftLookupMaps={rosterShiftLookupMaps}
                      manPower_monthly={manPower_monthly}
                      useMaxManpower={useMaxManpower}
                      year={curDate?.year ?? 0}
                      month={curDate?.month ?? 0}
                      showManpowerButton={true}
                      memo={memo}
                      setMemo={setMemo}
                      isEditableMemo={true}
                      copyNurses={statsNurses}
                      hasPrevTail={!!effectivePrevTailData}
                      hideViolationToggleRow
                      hideViolationFloating
                      rawValidateList={rawValidateListFromHook}
                      isValidating={isRosterValidating}
                      phase={rosterValidationPhase}
                      violationState={{
                        showManpowerViolations,
                        showConfigViolations,
                        showWantedViolations,
                        toggleManpowerViolations,
                        toggleConfigViolations,
                        toggleWantedViolations,
                      }}
                      role={
                        isAdmin ? "admin" : isHeadNurse ? "head_nurse" : "nurse"
                      }
                      onNavigateConfig={() => navigate("/roster_configure")}
                      showPrecepteeBadge={
                        precepteePolicy.excludePrecepteeFromCount
                      }
                    />
                  </div>
                  <RosterViolationFloatingControls
                    rawValidateList={rawValidateListFromHook}
                    isValidating={isRosterValidating}
                    phase={rosterValidationPhase}
                    violationState={{
                      showManpowerViolations,
                      showConfigViolations,
                      showWantedViolations,
                      toggleManpowerViolations,
                      toggleConfigViolations,
                      toggleWantedViolations,
                    }}
                  />
                </div>
                <div className=" flex gap-[10px]">
                  <TableHeader
                    defaultShiftCode={defaultShiftCode}
                    copyNurses={copyNursesWithWanted}
                    setCopyNurses={setCopyNurses}
                    currentYear={curDate?.year ?? 0}
                    currentMonth={curDate?.month ?? 0}
                    holidayDates={holidayDates}
                    prevTailData={effectivePrevTailData}
                  />
                  <div className="size-10 relative flex items-end">
                    {/*통계 - 헤더*/}
                    <div className=" flex absolute -bottom-[17px] -left-[1px] bg-white rounded-full">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className={cn(
                              "group flex size-7 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0",
                              "transition-opacity hover:opacity-90",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
                            )}
                            aria-pressed={showPerShiftStats}
                            aria-label={
                              showPerShiftStats
                                ? "개별 근무코드 통계 숨기기"
                                : "개별 근무코드 통계 보기"
                            }
                            onClick={() =>
                              setShowPerShiftStats((prev) => !prev)
                            }
                          >
                            <img
                              src="/asset/icon/config/icon_1.svg"
                              alt=""
                              aria-hidden
                              className={cn(
                                "size-7 object-contain transition-[filter,transform] duration-200 ease-out brightness-100 group-hover:brightness-[0.55]",
                                showPerShiftStats ? "rotate-90" : "-rotate-90"
                              )}
                              draggable={false}
                            />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {showPerShiftStats
                              ? "개별 근무코드 통계 숨기기"
                              : "개별 근무코드 통계 보기"}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
              {showPerShiftStats && (
                <div className="mb-2.5 animate-in fade-in duration-200 -translate-x-[0px] pr-2">
                  <RosterShiftLegendChips
                    displayShifts={rosterCreateShiftLegend}
                    defaultShiftCode={defaultShiftCode}
                  />
                </div>
              )}
            </div>

            {/* Roster Summary */}
            <div className="relative pb-30 px-5">
              <ContainerLoadingOverlay
                isOpen={
                  isLoading ||
                  isDateChanging ||
                  isVersionChanging ||
                  shouldShowWantedAdjustmentLoadingOverlay
                }
                title={
                  isLoading || isDateChanging || isVersionChanging ? (
                    <>
                      {curDate?.year}년 {curDate?.month}월
                      <span className="text-sig">
                        {curScheduleInfo?.version
                          ? ` ver  ${curScheduleInfo?.version}`
                          : ""}
                      </span>{" "}
                      근무표를 가져오는 중입니다.
                    </>
                  ) : (
                    <>
                      {curDate?.year}년 {curDate?.month}월 원티드 반영사항을
                      가져오는 중입니다.
                    </>
                  )
                }
              />

              <div
                className={cn(
                  "animate-in fade-in-0 duration-300 transition-opacity relative"
                )}
              >
                {/* 개별 근무표 보기임 */}
                {currentTeamTableViewTab === 0 && (
                  <div className="relative">
                    {/* 근무 반영 로직 */}
                    <TableBody
                      mode="CREATE"
                      copyNurses={copyNursesWithWanted}
                      setCopyNurses={setCopyNurses}
                      availableShifts={availableShifts}
                      isEditMode={uiMode === "edit"}
                      selectedShift={selectedShift}
                      modDayShift={modDayShift}
                      enableRowDragSort={false}
                      saveNurseSequence={async () => {}}
                      currentScheduleInfo={currentScheduleInfoMemo}
                      currentScheduleID={curScheduleInfo?.scheduleId}
                      isDragEditing={isDragEditing}
                      selectedGroupId={selectedGroupId}
                      manPower_monthly={manPower_monthly}
                      manPower_daily={manPower_daily}
                      useMaxManpower={useMaxManpower}
                      enableMaxManpowerViolation={ENABLE_MAX_MANPOWER_VIOLATION}
                      year={curDate?.year ?? 0}
                      month={curDate?.month ?? 0}
                      selectIdForSideprofile={handleSelectIdForSideProfile}
                      prevTailData={effectivePrevTailData}
                      isEmergencyPickMode={
                        uiMode === "emergency" && currentTeamTableViewTab === 0
                      }
                      onEmergencySlotPick={(pick) =>
                        handleEmergencyReplacementCellPick(
                          pick,
                          emergencyBulkHasResult,
                          emergencySingleHasResult
                        )
                      }
                      emergencySlotPick={emergencySlotPick}
                      isEmergencyBulkPickMode={false}
                      emergencyBulkPeriodPick={emergencyBulkPeriodPick}
                      emergencyBulkSelectedSteps={emergencyBulkSelectedSteps}
                      emergencyReplacementCandidateNurseId={
                        emergencyReplacementCandidateNurseId
                      }
                      emergencyPostApplyHighlight={emergencyPostApplyHighlight}
                      showPerShiftStats={showPerShiftStats}
                      schedulePayloadYear={rosterData?.year}
                      schedulePayloadMonth={rosterData?.month}
                      currentGroupId={
                        isAdmin
                          ? (selectedGroupId ?? null)
                          : (loginUser?.group_id ?? null)
                      }
                    />
                  </div>
                )}

                {/* 팀별 근무표 보기임 */}
                {currentTeamTableViewTab === 1 && (
                  <Roster_create_Tab2
                    copyNurses={copyNursesWithWanted}
                    setCopyNurses={setCopyNurses}
                    fullNurseSchema={fullNurseSchema}
                    setFullNurseSchema={setFullNurseSchema}
                    fullNurseSchemaAddPreceptor={
                      fullNurseSchemaAddPreceptor ?? []
                    }
                    availableShifts={availableShifts}
                    isEditMode={uiMode === "edit"}
                    selectedShift={selectedShift}
                    modDayShift={modDayShift}
                    saveNurseSequence={async () => {}}
                    prevTailData={effectivePrevTailData}
                    showPerShiftStats={showPerShiftStats}
                    cellViolationMap={rosterCellViolationMap}
                    selectIdForSideprofile={handleSelectIdForSideProfile}
                    scheduleYear={curDate?.year ?? 0}
                    scheduleMonth={curDate?.month ?? 0}
                    schedulePayloadYear={rosterData?.year}
                    schedulePayloadMonth={rosterData?.month}
                    currentGroupId={
                      isAdmin
                        ? (selectedGroupId ?? null)
                        : (loginUser?.group_id ?? null)
                    }
                    isEmergencyPickMode={
                      uiMode === "emergency" && currentTeamTableViewTab === 1
                    }
                    onEmergencySlotPick={(pick) =>
                      handleEmergencyReplacementCellPick(
                        pick,
                        emergencyBulkHasResult,
                        emergencySingleHasResult
                      )
                    }
                    emergencySlotPick={emergencySlotPick}
                    isEmergencyBulkPickMode={false}
                    emergencyBulkPeriodPick={emergencyBulkPeriodPick}
                    emergencyBulkSelectedSteps={emergencyBulkSelectedSteps}
                    emergencyReplacementCandidateNurseId={
                      emergencyReplacementCandidateNurseId
                    }
                    emergencyPostApplyHighlight={emergencyPostApplyHighlight}
                  />
                )}
              </div>
            </div>
          </div>

          {uiMode === "edit" && (
            <ModalRosterModify
              modalClose={(open) => {
                if (!open) setUiMode("none");
              }}
              targetRef={rosterModifyButtonRef as RefObject<HTMLElement | null>}
              availableShifts={availableShifts}
              selectedShift={selectedShift}
              setSelectedShift={setSelectedShift}
              deleteAllShift={deleteAllShift}
              onUndo={handleUndo}
              onRedo={handleRedo}
              canUndo={canUndo}
              canRedo={canRedo}
            />
          )}

          {uiMode === "deadline" && wantedStatus && (
            <SetDeadLine
              setIsOpendDeadlineModal={(open) =>
                setUiMode(open ? "deadline" : "none")
              }
              currentScheduleInfo={currentScheduleInfoMemo}
              wantedStatus={wantedStatus}
              fetchWantedStatus={handleFetchWantedStatus}
              setIsOpendModalSubmittedWantedlistModal={(open) =>
                setUiMode(open ? "wanted" : "none")
              }
              targetRef={wantedButtonRef}
              clearHistory={clearHistory}
            />
          )}

          {uiMode === "wanted" && (
            <ModalRosterWanted
              defaultShiftCode={defaultShiftCode}
              currentScheduleInfo={currentScheduleInfoMemo}
              holidayDates={holidayDates}
              shiftInfo={availableShifts}
              nurses={
                (fullNurseSchemaAddPreceptor ||
                  fullNurseSchema) as NurseResponse[]
              }
              onClose={handleModalRosterWantedClose}
              isViewOnlyOff={false}
              initialTab={wantedModalInitialTab}
              showManpowerButton={false} // 원티드 모달: 인력 설정 버튼 숨김(uiMode 충돌 방지). 최소인원 숫자는 manPower_monthly
              manPower_monthly={manPower_monthly}
              manPower_daily={manPower_daily}
              memo={memo}
              setMemo={setMemo}
              isEditableMemo={false}
              currentScheduleID={curScheduleInfo?.scheduleId}
              prevTailData={effectivePrevTailData}
            />
          )}

          {uiMode === "manpower" && (
            <ModalManpower
              onClose={() => setUiMode("none")}
              availableShifts={availableShifts}
              defaultShiftCode={defaultShiftCode}
              holidayDates={holidayDates}
              officeId={resolvedManpowerOid ?? ""}
              groupId={resolvedManpowerGid ?? ""}
              year={manpowerYear ?? 0}
              month={manpowerMonth ?? 0}
              initialMonthly={manPower_monthly ?? ({} as type.ManPowerMonthly)}
              initialDaily={manPower_daily ?? ({} as type.ManPowerDaily)}
              initialMaxEnabled={useMaxManpower}
              currentShiftManage={currentShiftManage ?? []}
              totalNursesCount={copyNurses.length}
            />
          )}

          {/* OFF 요청 현황 모달 */}
          {uiMode === "offRequest" && curDate?.year && curDate?.month && (
            <ModalOffRequestList
              defaultShiftCode={defaultShiftCode}
              currentScheduleInfo={{
                year: currentScheduleInfoMemo.year,
                month: currentScheduleInfoMemo.month,
              }}
              holidayDates={holidayDates}
              nurses={fullNurseSchema}
              shiftInfo={availableShifts}
              fullWanted={fullWanted}
              onClose={() => setUiMode("none")}
            />
          )}

          {/* 사이드 프로필 모달 */}
          {isSideProfileOpen && (
            <NurseEditSideProfile
              onlyView={true}
              isOpen={isSideProfileOpen}
              onClose={() => {
                setIsSideProfileOpen(false);
                setActiveNurseIdForSideProfile(null);
              }}
              selectedGroupId={selectedGroupId}
              isAdmin={isAdmin}
              nurseId={activeNurseIdForSideProfile}
              allNurses={fullNurseSchema}
            />
          )}
        </div>
        {!!blockingInfeasibility && (
          <CustomModal.Alert
            isOpen={!!blockingInfeasibility}
            onClose={() => {
              setBlockingInfeasibility(null);
              setRosterInfeasibility(null);
            }}
            confirmText="닫기"
            dismissOnOverlay={false}
            className="w-[560px] max-w-[calc(100vw-300px)] max-h-[calc(100vh-140px)]"
          >
            <div className="font-pretendard">
              <h3 className="mb-3 text-center text-[20px] font-semibold text-[#1D1D1D]">
                근무표 생성을 실패하였습니다.
              </h3>
              <p className="mb-4 whitespace-pre-wrap rounded-[6px] bg-destructive/8 break-keep px-3 py-2 text-[14px] leading-relaxed text-destructive">
                {blockingInfeasibility.summary_message_ko}
              </p>

              {blockingInfeasibility.preflight_issues.length > 0 && (
                <div className="mb-4">
                  <p className="mb-2 text-[14px] font-semibold text-foreground">
                    발견된 문제 ({blockingInfeasibility.preflight_issues.length}
                    )
                  </p>
                  <ul className="max-h-[180px] space-y-2 overflow-y-auto rounded-[6px] border  p-3">
                    {blockingInfeasibility.preflight_issues.map(
                      (issue, index) => (
                        <li
                          key={`${issue.reason_code}-${index}`}
                          className="text-[13px]  text-[#53535A]"
                        >
                          {issue.human_message_ko}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}

              {blockingInfeasibility.fix_suggestions_ko.length > 0 && (
                <div className="mb-5 mt-9">
                  <p className="mb-2 text-[14px] font-semibold text-foreground">
                    다음 중 하나를 시도해보세요
                  </p>
                  <ul className="space-y-1 rounded-[6px] bg-muted/50 p-3">
                    {blockingInfeasibility.fix_suggestions_ko.map(
                      (suggestion, index) => (
                        <li
                          key={`${suggestion}-${index}`}
                          className="text-[13px] font-pretendard  text-muted-foreground"
                        >
                          {suggestion}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}

              {/* {blockingInfeasibility.last_error_reason && (
                <p className="mb-5 whitespace-pre-wrap rounded-[6px] break-keep bg-zinc-50 p-3 text-sm leading-relaxed text-zinc-500">
                  {blockingInfeasibility.last_error_reason}
                </p>
              )} */}
            </div>
          </CustomModal.Alert>
        )}
        {/* 근무표 생성 상태 분리 */}

        <RosterIndicater
          resetGenerateRoster={resetGenerateRoster}
          generatedVersion={curScheduleInfo?.version}
        />
      </div>
    </ModifiedCellsProvider>
  );
}
export default Roster_create;
